import React, { useMemo } from 'react';
import { Table, Button, Modal, Dropdown } from 'antd';
import { MoreOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PatientSessionsEmptyState from "../patient/PatientSessionsEmptyState";
import deleteSession from '../../utils/session/deleteSession';
import { logEvent } from '../../analytics';

export default function SessionList({ sessions, pagination, onPageChange, fromPatientDetails = false }) {
  const { t } = useTranslation();

  const handleDelete = async (session) => {
    const isPatientSession = session.getPatient();
    
    Modal.confirm({
      title: t(isPatientSession 
        ? 'sessions.fields.delete_patient_session_confirmation_title'
        : 'sessions.fields.delete_confirmation_title'),
      content: t(isPatientSession
        ? 'sessions.fields.delete_patient_session_confirmation_content'
        : 'sessions.fields.delete_confirmation_content'),
      okText: t(isPatientSession
        ? 'sessions.fields.delete_patient_session_confirmation_ok'
        : 'sessions.fields.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        logEvent('Sessions', 'Delete');
        const success = await deleteSession(session.getUUID());
        if (success) {
          onPageChange(pagination.current);
        }
      }
    });
  };

  const buildSessionLink = (record) => {
    const basePath = `/sessions/${record.getUUID()}`;
    const searchParams = new URLSearchParams();

    if (!record.isReady()) searchParams.set('generating', '1');

    return searchParams.toString() ? `${basePath}?${searchParams.toString()}` : basePath;
  };

  const columns = useMemo(() => [
    {
      title: t('sessions.title'),
      dataIndex: 'gen_title',
      key: 'gen_title',
      render: (text, record) => {
        if (record.isErrored())  {
          const no_note_title = t('sessions.fields.no_note');
          return <span style={{ color: 'grey' }}>{no_note_title}</span>
        }

        return (
          <Link to={buildSessionLink(record)}>
            {record.getTitle(t, fromPatientDetails)}
          </Link>
        );
      },
      onCell: () => ({ style: { minWidth: 200 } }),
    },
    {
      title: '',
      dataIndex: 'summary',
      key: 'summary',
      responsive: ['lg'],
      render: (text, record) => {
        const excerpt = record.getExcerpt();
        return record.isErrored() ? <span style={{ color: 'grey' }}>{excerpt}</span> : excerpt;
      },
    },
    {
      title: '',
      dataIndex: 'last_session_date',
      key: 'last_session_date',
      responsive: ['md'],
      render: (text, record) => record.getLastSessionDate(),
      onCell: () => ({ style: { minWidth: 200 } }),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'delete',
                label: t('sessions.fields.delete'),
                icon: <DeleteOutlined />,
                onClick: () => handleDelete(record),
                danger: true
              }
            ]
          }}
          trigger={['hover']}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            style={{ color: 'grey' }}
          />
        </Dropdown>
      ),
    },
  ], [t, pagination.current]);

  if (sessions.length === 0) return (
    <PatientSessionsEmptyState />
  );

  return (
    <Table
      showHeader={false}
      columns={columns}
      dataSource={sessions}
      rowKey={(record) => record.getUUID()}
      pagination={
        pagination.total > pagination.pageSize
          ? {
            ...pagination,
            position: ['bottomCenter']
         }
          : false
      }
      onChange={(pagination) => onPageChange(pagination.current)}
    />
  );
}